import React, { useEffect, useState, useCallback } from 'react';
import { ProductType } from '@inter-ikea-storageone/range';
import { changeTV } from '../behaviors/changeTV';
import Button from '@ingka/button';
import CrossIcon from '@ingka/ssr-icon/paths/cross';
import IncorrectIcon from '@ingka/ssr-icon/paths/incorrect';
import { ComponentIds, MetaDataComponent } from '@inter-ikea-core/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import Box from 'ui/Box';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import KompisToolTip from 'ui/KompisToolTip/KompisToolTip';
import Title, { HeadingType } from 'ui/Typography/Title';
import media from 'ui/media';

import '@ingka/button/dist/style.css';
import '@ingka/focus/dist/style.css';
import '@ingka/modal/dist/style.css';
import '@ingka/svg-icon/dist/style.css';
import MobileDrawer from 'components/MobileDrawer/MobileDrawer';
import SlideOutMenu from 'components/SlideOutMenu/SlideOutMenu';
import { isMobile } from 'utils/platform';
import { store } from 'ducks';
import { useLocation } from 'react-router-dom';
import { AppRoutes } from 'routes';
import { getTvSize, setTvSize, tvBenchProductTypes, TvPlacement, tvSizes } from '../TVFeature';
import { useSelector } from 'react-redux';
import { getRange } from 'utils/queryUtils';
import SegmentedControl from '@ingka/segmented-control';

const SelectedMenuWrapper = styled.div`
  height: 100%;
  padding: 0 ${({ theme }) => theme.space.xxl};
  overflow-y: auto;
  ${media.mobile`
    padding: 0 1rem;
  `}
`;

const HeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 200;
  background: #fff;
  padding-top: ${({ theme }) => theme.space.md};
  padding-bottom: ${({ theme }) => theme.space.xs};
`;

const HeaderFlex = styled.div`
  display: flex;
  align-items: center;
  &[data-space-between='true'] {
    justify-content: space-between;
  }
`;

const SizeGrid = styled.div`
  margin-top: ${({ theme }) => theme.space.l};
  margin-bottom: ${({ theme }) => theme.space.xl};
  display: grid;
  border: 0;
  grid-template-columns: repeat(auto-fit, ${({ theme }) => theme.space.xxxxl});
  grid-template-rows: max-content;
  height: max-content;
  grid-gap: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xs};
  justify-content: left;
  width: 100%;
  padding: 0;
  ${media.mobile`
    margin: 1rem 0 0;
  `}
  &[data-selected='true'] {
    border: 2px solid black;
    padding: 7px;
    box-sizing: content-box;
  }
`;

const TvSizeRadioButtonLabel = styled.label`
  padding: 7px;
  box-sizing: content-box;
  cursor: pointer;

  &[data-selected='true'] {
    border-bottom: 2px solid black;
  }

  [type='radio'] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const TvSizeOptionWrapper = styled.div`
  margin-left: 1.4rem;
  padding: 0 0 ${({ theme }) => theme.space.l};
`;

const TvSizeRadioButton = styled.input``;

const MobileWrapper = styled.div`
  padding: ${({ theme }) => theme.space.mdl};
`;

const TvChoiceComponent = ({ activeIndex, onChangeIndex, disableFootTV }: any) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <Title mb="md" type={ThemeFontStyleTypeEnum.headingSmall} heading={HeadingType.HeadingTwo}>
        {formatMessage({ id: 'storageone.customiseTvSheetTitle' })}
      </Title>
      <SegmentedControl
        items={[
          {
            label: formatMessage({ id: 'storageone.addTvSheetChoiceWall' })
          },
          {
            label: formatMessage({ id: 'storageone.addTvSheetChoiceStanding' }),
            disabled: disableFootTV
          }
        ]}
        data-testid="change-tv-type-toggle"
        activeIndex={activeIndex}
        onClick={(eventObj, index) => onChangeIndex(index)}
      />
      {disableFootTV && (
        <Box mt="l">
          <SkapaInlineMessage
            body={formatMessage({ id: 'storageone.addTvSheetErrorMsg' })}
            variant={InlineMessageVariantEnum.NEGATIVE}
            ssrIcon={IncorrectIcon}
            subtle
          />
        </Box>
      )}
    </>
  );
};

const TvSizeComponent = ({ tvSize, onChangeTVSize }: any) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <Title type={ThemeFontStyleTypeEnum.headingSmall} heading={HeadingType.HeadingTwo}>
        {formatMessage({ id: 'storageone.addTvSheetHeaderSize' })}
      </Title>
      <SizeGrid role="group">
        {tvSizes.map(size =>
          isMobile() ? (
            <TvSizeOptionWrapper key={size}>
              <TvSizeRadioButtonLabel data-selected={size === tvSize}>
                <TvSizeRadioButton
                  data-testid={`change-tvsize-${size}`}
                  data-selected={size === tvSize}
                  type="radio"
                  name="size-radio"
                  value={size}
                  checked={size === tvSize}
                  onChange={() => onChangeTVSize(size)}
                />
                {size}”
              </TvSizeRadioButtonLabel>
            </TvSizeOptionWrapper>
          ) : (
            <KompisToolTip text={`${size}"`} uniqueId={`${size}`} key={size}>
              <TvSizeRadioButtonLabel key="size-radio" data-selected={size === tvSize}>
                <TvSizeRadioButton
                  data-testid={`change-tvsize-${size}`}
                  data-selected={size === tvSize}
                  type="radio"
                  name="size-radio"
                  value={size}
                  checked={size === tvSize}
                  onChange={() => onChangeTVSize(size)}
                />
                {size}”
              </TvSizeRadioButtonLabel>
            </KompisToolTip>
          )
        )}
      </SizeGrid>
    </>
  );
};

function ChangeTVSheet() {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const coreApi = useCore();
  const rootState = store.getState();
  const tvSize = useSelector(getTvSize);
  // If current page is summary, we should hide the mobile drawer as that causes the summary page to not be usable on mobile devices
  const currentPageIsSummary = useLocation().pathname === AppRoutes.SUMMARY;
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [sheetVisible, setSheetVisible] = useState<boolean>(false);
  const [disableFootTV, setDisableFootTV] = useState<boolean>(true);
  const removeFootTvOption = getRange() === 'eket';

  const adaptTv = (tvSize: number, activeIndex: number) =>
    changeTV({
      dispatch,
      coreApi,
      props: {
        tvSize,
        tvPlacement: activeIndex === 0 ? TvPlacement.wall : TvPlacement.foot
      },
      rootState
    });

  const onChangeTVSize = (size: number) => adaptTv(size, activeIndex);
  const onChangeIndex = (index: number) => tvSize && adaptTv(tvSize, index);
  const onCloseSheet = () => {
    setSheetVisible(false);

    const selectedEntity = coreApi.entities.findSelectedEntity();
    const isTvSelected =
      selectedEntity &&
      coreApi.entities.isOneOfProductTypes(selectedEntity, [
        ProductType.TV,
        ProductType.TV_WITH_FOOT
      ]);

    if (selectedEntity && isTvSelected)
      selectedEntity.removeComponent(ComponentIds.SELECTED_COMPONENT_ID);
  };

  const entitySelected = useCallback(() => {
    const selectedEntity = coreApi.entities.findSelectedEntity();

    if (!selectedEntity) return;

    const isWallTv = coreApi.entities.isProductType(selectedEntity, ProductType.TV);
    const isFootTv = coreApi.entities.isProductType(selectedEntity, ProductType.TV_WITH_FOOT);
    const tvBench = coreApi.entities.getByProductTypes(tvBenchProductTypes);

    if (isWallTv || isFootTv) {
      setDisableFootTV(!tvBench.length);
      setActiveIndex(isWallTv ? 0 : 1);
      setSheetVisible(true);

      const tvSizeInScene = selectedEntity.getComponent<MetaDataComponent>(
        ComponentIds.META_DATA_COMPONENT_ID
      )?.metaData.tvSize;

      if (tvSizeInScene) dispatch(setTvSize(tvSizeInScene as number));
    }
  }, [coreApi.entities, dispatch]);

  const entityUnselected = useCallback(() => {
    setSheetVisible(false);
  }, [setSheetVisible]);

  useEffect(() => {
    coreApi
      .getServices()
      .entityManager.subscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, entitySelected);
    coreApi
      .getServices()
      .entityManager.subscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, entityUnselected);
    return () => {
      coreApi
        .getServices()
        .entityManager.unsubscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, entitySelected);
      coreApi
        .getServices()
        .entityManager.unsubscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, entityUnselected);
    };
  }, [coreApi, entitySelected, entityUnselected]);

  return isMobile() ? (
    <MobileDrawer
      open={sheetVisible && !currentPageIsSummary}
      title={formatMessage({ id: 'storageone.customiseTvSheetTitle' })}
      onDismiss={() => onCloseSheet()}
      data-testid="change-tv-modal"
    >
      <MobileWrapper>
        <TvSizeComponent tvSize={tvSize} onChangeTVSize={onChangeTVSize} />
        <TvChoiceComponent
          activeIndex={activeIndex}
          onChangeIndex={onChangeIndex}
          disableFootTV={disableFootTV}
        />
      </MobileWrapper>
    </MobileDrawer>
  ) : (
    <SlideOutMenu trigger={sheetVisible} data-testid="change-tv-modal">
      <SelectedMenuWrapper>
        <HeaderWrapper>
          <HeaderFlex data-space-between>
            <Title type={ThemeFontStyleTypeEnum.headingMedium}>
              {formatMessage({ id: 'storageone.customiseTvSheetTitle' })}
            </Title>
            <Button
              data-testid="add-tv-close-button"
              onClick={onCloseSheet}
              ssrIcon={CrossIcon}
              aria-label={formatMessage({ id: 'storageone.closeButton' })}
              type="tertiary"
              iconOnly
            />
          </HeaderFlex>
        </HeaderWrapper>
        <TvSizeComponent tvSize={tvSize} onChangeTVSize={onChangeTVSize} />
        {!removeFootTvOption && (
          <TvChoiceComponent
            activeIndex={activeIndex}
            onChangeIndex={onChangeIndex}
            disableFootTV={disableFootTV}
          />
        )}
      </SelectedMenuWrapper>
    </SlideOutMenu>
  );
}

export default ChangeTVSheet;
